import { action, makeObservable, observable } from 'mobx';
import { IScheduleOptions } from 'stores/settings/SettingsStore';

export enum PolicyType {
    Ransomware = 'ransomware',
    Defender = 'defender',
    Av = 'av',
    Edr = 'edr',
    Response = 'response'
}

export enum PolicyTypeName {
    Ransomware = 'Ransomware Detection',
    Defender = 'Windows Defender',
    Av = 'Automated Response',
    Edr = 'Datto Antivirus',
    Response = 'Datto EDR'
}

export enum Fileset {
    All = 'all',
    Recommended = 'recommended'
}

// TODO: remove mapper where it uses, policy (actually policy_detail view) has typeName field with all these values
export const policyTypeToLabel: any = {
    [PolicyType.Ransomware]: 'Ransomware Detection',
    [PolicyType.Defender]: 'Windows Defender',
    [PolicyType.Response]: 'Automated Response',
    [PolicyType.Av]: 'Datto Antivirus',
    [PolicyType.Edr]: 'Datto EDR'
};

export interface IPolicy {
    id?: string;
    isSelected?: boolean;
    name: string;
    description: string;
    type: PolicyType | string;
    typeName?: PolicyTypeName;
    active: boolean;
    disabled: boolean;
    data?: any;
    createdOn?: string;
    updatedOn?: string;
}

export enum RbCacheSizeType {
    relative = 'pct',
    absolute = 'gb'
}

export interface IResponsePolicy extends IPolicy {
    data?: {
        recommendedResponses?: boolean;
    };
    type: PolicyType.Response;
}

export interface RansomwarePolicy extends IPolicy {
    data?: {
        additionalPaths?: [];
        excludeExtension?: [];
        isolateHost?: boolean;
        localDrive?: boolean;
        killProcess?: boolean;
        shutdownHost?: boolean;
        lockVolumesOnRwAttack?: boolean;
        rbEnabled?: boolean;
        rbTrackAdditionalVolumes?: boolean;
        rbExcludeStandardWindowsFolder?: boolean;
        rbIncludeFolders?: [];
        rbExcludeFolders?: [];
        rbMaxTrackHistoryHours?: number;
        rbMinTrackHistoryHours?: number;
        rbCacheSizeType?: RbCacheSizeType;
        rbCacheSizeValue: number;
        rbAlertOnFastCacheExhaust: boolean;
        rbLockVolumes: boolean;
    };
    type: PolicyType.Ransomware;
}

export interface DefenderPolicy extends IPolicy {
    data?: {
        uiSettings?: {
            uiLockdown?: boolean;
            useProxyServer?: boolean;
            proxyServer?: string;
            cloudProtection?: boolean;
            monitorFile?: boolean;
            dnsSinkHole?: boolean;
            behaviorMonitoring?: boolean;
            networkInspection?: boolean;
            scanHeuristics?: boolean;
            scriptScanning?: boolean;
            serviceKeepAlive?: boolean;
            emailScanning?: boolean;
            scheduleScan?: boolean;
            scheduleDay?: string;
            scanType?: string;
            scanScheduleTime?: string;
            catchupScan?: boolean;
            catchupScanType?: string;
            catchupThreshold?: string;
            scanNetworkFiles?: boolean;
            scanNtfsSymbolicLinks?: boolean;
            excludeExtension?: [];
            excludePath?: [];
            excludeProcess?: [];
            asrExcludeProcess?: [];
            ransomwareProtection?: boolean;
            blockUntrustedProcessFromUsb?: boolean;
            officeReaderProtection?: boolean;
            blockVulnerableDrivers?: boolean;
            blockMalwareTechniques?: boolean;
            protectionLevel?: string;
        };
    };
    type: PolicyType.Defender;
}

export interface AvPolicy extends IPolicy {
    data?: {
        realtimeScansEnabled: boolean;
        rtArchiveScansEnabled: boolean;
        rtNetworkScansEnabled: boolean;
        rtBehavioralDetectionEnabled: boolean;
        rtArchiveNestedLimit: string;
        rtArchiveFileLimit: string;
        rtArchivesMaxRatio: string;
        rtArchiveFilesizeLimit: string;
        rtExcludedFolders: Array<string>;
        rtExcludedFiles: Array<string>;
        rtExcludedProcesses: Array<string>;
        scheduledFileset: string;
        scheduledArchiveScansEnabled: boolean;
        scheduledArchiveNestedLimit: string;
        scheduledArchiveIncludeFormatsMailbox: boolean;
        scheduledArchiveIncludeFormatsDiskImage: boolean;
        scheduledExcludedFolders: Array<string>;
        scheduledExcludedFiles: Array<string>;
        scheduledFullScan: boolean;
        scheduledAlertOnly: boolean;
        fullScanScheduleOptions: object;
        scheduledQuickScan: boolean;
        quickScanScheduleOptions: object;
        debugLogging: boolean;
        dnsSecureEnabled: boolean;
        dnsSecurityCategories: any;
        dnsContentCategories: any;
        dnsBlockedDomainList: Array<any>;
        dnsTrustedDomainList: Array<any>;
        dnsTrustedExecutablesList: Array<any>;
        includeAvExclusion: boolean;
    };
    type: PolicyType.Av;
}

export interface EdrPolicy extends IPolicy {
    data?: {
        rtsEnabled: boolean;
        scheduledScan: boolean;
        scheduleOptions: IScheduleOptions;
        schedule: string;
        timezone: string;
        scanOptions: any;
        extensions: any;
        tamperProtectionEnabled: boolean;
        rtsProcesses: boolean;
        rtsMemscan: boolean;
        rtsNetwork: boolean;
        rtsAutostarts: boolean;
        rtsMemscanExcludedProcesses: Array<string>;
    };
    type: PolicyType.Edr;
}

export default class PolicyStore {
    policy: IPolicy = {
        name: '',
        description: '',
        type: '',
        active: false,
        disabled: false
    };

    constructor(policy: IPolicy & { author?: { email: string } }) {
        this.update(policy);

        makeObservable(this, {
            policy: observable,
            update: action
        });
    }

    update(policy: IPolicy & { author?: { email: string } }): void {
        this.policy = {
            ...this.policy,
            ...policy
        };
    }
}
